import React, { useEffect, useState, useRef, useMemo } from "react";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import fivePaisaMarketDepthStore from "../services/fivePaisaMarketDepthStore";
import angelOneWebSocketService, {
  getAngelExchangeTypeFromHolding,
} from "../services/angelOneWebSocketService";
import angelOneMarketDepthService from "../services/angelOneMarketDepthService";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiX } from "react-icons/fi";
import RealCandlestickChart from "./RealCandlestickChart";
import StockPreviewMarketSnapshotPanel from "./StockPreviewMarketSnapshotPanel";
import stockPreviewService from "../services/stockPreviewService";
import SectionInfoIcon from "./SectionInfoIcon";

const PMS_API_BASE =
  process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";

const RANGE_MAP = {
  "1D": 0,
  "1W": 7,
  "1M": 30,
  "6M": 180,
};

const ANGEL_INTERVAL_MAP = {
  "1m": "ONE_MINUTE",
  "5m": "FIVE_MINUTE",
  "10m": "TEN_MINUTE",
  "15m": "FIFTEEN_MINUTE",
  "30m": "THIRTY_MINUTE",
  "60m": "ONE_HOUR",
  "1d": "ONE_DAY",
};

const DEPTH_THEME = {
  primary: "#2457BF",
  primaryDark: "#1A42A0",
  primarySoft: "#EFF4FF",
  primarySoftAlt: "#DCE8FF",
  primaryText: "#2457BF",
  primaryTint: "rgba(36, 87, 191, 0.04)",
  primaryTintStrong: "rgba(36, 87, 191, 0.08)",
};

const formatDate = (date) => date.toISOString().split("T")[0];

const formatAngelDateTime = (date, hour, minute) => {
  const datePart = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  const h = String(hour).padStart(2, "0");
  const m = String(minute).padStart(2, "0");
  return `${datePart} ${h}:${m}`;
};

const getAngelDateRange = (range) => {
  const days = RANGE_MAP[range] || 0;
  const today = new Date();
  const from = new Date();
  from.setDate(today.getDate() - days);

  return {
    fromdate: formatAngelDateTime(from, 9, 15),
    todate: formatAngelDateTime(today, 15, 30),
  };
};

const resolveAngelExchange = (Exch, ExchSeg) => {
  const seg = String(ExchSeg || "").toUpperCase();
  const exch = String(Exch || "").toUpperCase();
  if (exch.startsWith("B") || seg.startsWith("BSE")) return "BSE";
  if (exch === "M" || seg.includes("MCX")) return "MCX";
  return "NSE";
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  const number = parseFloat(num);
  if (isNaN(number)) return "-";

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getFromDate = (range) => {
  const today = new Date();
  const days = RANGE_MAP[range] || 0;
  const from = new Date();
  from.setDate(today.getDate() - days);
  return formatDate(from);
};

const getToDate = () => formatDate(new Date());

const toPmsBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (
    normalized === "grow" ||
    normalized === "5paisa" ||
    normalized === "angelone"
  ) {
    return normalized;
  }
  return "5paisa";
};

const formatTime = (time, range) => {
  const date = new Date(time);

  if (range === "1D") {
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const TIMEFRAME_MS = {
  "1m": 60 * 1000,
  "5m": 5 * 60 * 1000,
  "10m": 10 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "30m": 30 * 60 * 1000,
  "60m": 60 * 60 * 1000,
  "1d": 24 * 60 * 60 * 1000,
};

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

const getTimeframeMs = (timeframe) => TIMEFRAME_MS[timeframe] || null;

const getCurrentBucketStartMs = (tfMs, nowMs = Date.now()) => {
  if (!tfMs) return nowMs;
  // Align buckets to IST wall-clock (intraday TFs share minute boundaries with UTC+offset)
  const shifted = nowMs + IST_OFFSET_MS;
  return Math.floor(shifted / tfMs) * tfMs - IST_OFFSET_MS;
};

const getMsUntilNextBucket = (tfMs, nowMs = Date.now()) => {
  if (!tfMs) return 60 * 1000;
  const shifted = nowMs + IST_OFFSET_MS;
  const elapsedInBucket = shifted % tfMs;
  const remaining = tfMs - elapsedInBucket;
  return Math.max(remaining, 300);
};

const applyLivePriceToCandles = (candles, livePrice) => {
  if (!Array.isArray(candles) || !candles.length) return candles;
  if (!Number.isFinite(livePrice) || livePrice <= 0) return candles;
  const updated = [...candles];
  const last = { ...updated[updated.length - 1] };
  last.close = livePrice;
  if (livePrice > last.high) last.high = livePrice;
  if (livePrice < last.low) last.low = livePrice;
  updated[updated.length - 1] = last;
  return updated;
};

const rollLiveCandleIfNeeded = (candles, livePrice, timeframe, range) => {
  const tfMs = getTimeframeMs(timeframe);
  if (!tfMs || tfMs >= TIMEFRAME_MS["1d"] || !candles?.length) {
    return applyLivePriceToCandles(candles, livePrice);
  }

  const price =
    Number.isFinite(livePrice) && livePrice > 0
      ? livePrice
      : Number(candles[candles.length - 1]?.close) || 0;
  if (!price) return candles;

  const bucketStart = getCurrentBucketStartMs(tfMs);
  const last = candles[candles.length - 1];

  // Still inside the latest candle bucket → keep LTP on last candle
  if (Number(last?.time) >= bucketStart) {
    return applyLivePriceToCandles(candles, price);
  }

  // TF boundary crossed → append a fresh live candle
  const nextCandle = {
    time: bucketStart,
    label: formatTime(bucketStart, range),
    open: price,
    high: price,
    low: price,
    close: price,
    volume: 0,
  };
  return [...candles, nextCandle];
};

const mergeFetchedCandlesWithLive = (
  fetched,
  previous,
  livePrice,
  timeframe,
  range,
) => {
  let merged = Array.isArray(fetched) ? [...fetched] : [];
  const tfMs = getTimeframeMs(timeframe);
  const prevLast = previous?.[previous.length - 1];
  const fetchedLast = merged[merged.length - 1];

  // Keep an optimistic live candle if API has not closed/opened it yet
  if (
    prevLast &&
    Number.isFinite(prevLast.time) &&
    (!fetchedLast || prevLast.time > fetchedLast.time)
  ) {
    merged = [...merged, prevLast];
  } else if (
    prevLast &&
    fetchedLast &&
    prevLast.time === fetchedLast.time &&
    Number.isFinite(livePrice) &&
    livePrice > 0
  ) {
    // Preserve live extremes built locally on the active candle
    const kept = { ...fetchedLast };
    kept.close = livePrice;
    kept.high = Math.max(
      Number(kept.high) || livePrice,
      livePrice,
      Number(prevLast.high) || livePrice,
    );
    kept.low = Math.min(
      Number(kept.low) || livePrice,
      livePrice,
      Number(prevLast.low) || livePrice,
    );
    merged = [...merged.slice(0, -1), kept];
    return merged;
  }

  if (tfMs) {
    merged = rollLiveCandleIfNeeded(merged, livePrice, timeframe, range);
  } else {
    merged = applyLivePriceToCandles(merged, livePrice);
  }
  return merged;
};

const transformCandles = (candles, range, brokerFlag) => {
  return candles.map((c) => {
    const rawTime = c[0];
    let time;
    if (brokerFlag === "angelone") {
      if (typeof rawTime === "number") {
        time = rawTime;
      } else {
        const raw = String(rawTime || "");
        time = new Date(
          raw.includes("+") || raw.endsWith("Z") ? raw : `${raw}+05:30`,
        ).getTime();
      }
    } else {
      time = new Date(`${rawTime}+05:30`).getTime();
    }

    return {
      time,
      label: formatTime(time, range),
      open: c[1],
      high: c[2],
      low: c[3],
      close: c[4],
      volume: c[5],
    };
  });
};

const buildCandleRequestBody = ({
  brokerFlag,
  range,
  timeframe,
  user_id,
  Exch,
  ExchType,
  ExchSeg,
  ScripCode,
  symbolToken,
}) => {
  if (brokerFlag === "angelone") {
    const { fromdate, todate } = getAngelDateRange(range);
    return {
      broker: "angelone",
      exchange: resolveAngelExchange(Exch, ExchSeg),
      symboltoken: String(symbolToken || ScripCode || "").trim(),
      interval: ANGEL_INTERVAL_MAP[timeframe] || "FIVE_MINUTE",
      fromdate,
      todate,
      user_id,
    };
  }

  return {
    broker: brokerFlag,
    Exch,
    ExchType,
    ScripCode,
    timeframe,
    from: getFromDate(range),
    to: getToDate(),
    user_id,
  };
};

const MarketDepthPanel = ({ depthRows = [] }) => {
  const theme = DEPTH_THEME;
  const rows = Array.isArray(depthRows) ? depthRows : [];
  const bids = rows.filter((d) => d.BbBuySellFlag === 66);
  const asks = rows.filter((d) => d.BbBuySellFlag !== 66);

  return (
    <div
      style={{
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #E8EAED",
        background: "#fff",
      }}
    >
      <table
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr>
            <th
              colSpan="3"
              style={{
                background: `linear-gradient(135deg, ${theme.primarySoft} 0%, ${theme.primarySoftAlt} 100%)`,
                color: theme.primaryText,
                fontSize: "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                padding: "0.75rem 0.4rem",
                borderBottom: `2px solid ${theme.primary}`,
                borderRight: "1px solid #E8EAED",
                textAlign: "center",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                Bid
                <SectionInfoIcon
                  text="Buy-side price levels: quantity, price, and number of orders at each bid level."
                  size={11}
                />
              </span>
            </th>
            <th
              colSpan="3"
              style={{
                background: `linear-gradient(135deg, ${theme.primarySoft} 0%, ${theme.primarySoftAlt} 100%)`,
                color: theme.primaryText,
                fontSize: "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                padding: "0.75rem 0.4rem",
                borderBottom: `2px solid ${theme.primary}`,
                textAlign: "center",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                Ask
                <SectionInfoIcon
                  text="Sell-side price levels: quantity, price, and number of orders at each ask level."
                  size={11}
                />
              </span>
            </th>
          </tr>
          <tr style={{ backgroundColor: "#F8F9FB" }}>
            {["Qty", "Price", "Ord", "Qty", "Price", "Ord"].map(
              (label, index) => (
                <th
                  key={`${label}-${index}`}
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    color: "#374151",
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                    padding: "0.55rem 0.25rem",
                    borderBottom: "1px solid #E8EAED",
                    borderRight: index < 5 ? "1px solid #E8EAED" : undefined,
                    textAlign: "center",
                    width: "16.66%",
                  }}
                >
                  {label}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, i) => {
            const bid = bids[i] || { Quantity: 0, Price: 0, NumberOfOrders: 0 };
            const ask = asks[i] || { Quantity: 0, Price: 0, NumberOfOrders: 0 };

            return (
              <tr key={i} style={{ borderBottom: i < 4 ? "1px solid #F3F4F6" : "none" }}>
                <td
                  style={{
                    fontWeight: 600,
                    color: theme.primaryText,
                    fontSize: "0.8rem",
                    padding: "0.65rem 0.25rem",
                    borderRight: "1px solid #F3F4F6",
                    backgroundColor: theme.primaryTint,
                    textAlign: "center",
                  }}
                >
                  {bid.Quantity || "0"}
                </td>
                <td
                  style={{
                    fontWeight: 700,
                    color: theme.primaryText,
                    fontSize: "0.85rem",
                    padding: "0.65rem 0.25rem",
                    borderRight: "1px solid #F3F4F6",
                    backgroundColor: theme.primaryTintStrong,
                    textAlign: "center",
                  }}
                >
                  {bid.Price ? formatNumber(bid.Price) : "0"}
                </td>
                <td
                  style={{
                    fontWeight: 600,
                    color: theme.primaryText,
                    fontSize: "0.75rem",
                    padding: "0.65rem 0.25rem",
                    borderRight: "1px solid #F3F4F6",
                    backgroundColor: theme.primaryTint,
                    textAlign: "center",
                  }}
                >
                  {bid.NumberOfOrders || "0"}
                </td>
                <td
                  style={{
                    fontWeight: 600,
                    color: theme.primaryText,
                    fontSize: "0.8rem",
                    padding: "0.65rem 0.25rem",
                    borderRight: "1px solid #F3F4F6",
                    backgroundColor: theme.primaryTint,
                    textAlign: "center",
                  }}
                >
                  {ask.Quantity || "0"}
                </td>
                <td
                  style={{
                    fontWeight: 700,
                    color: theme.primaryText,
                    fontSize: "0.85rem",
                    padding: "0.65rem 0.25rem",
                    borderRight: "1px solid #F3F4F6",
                    backgroundColor: theme.primaryTintStrong,
                    textAlign: "center",
                  }}
                >
                  {ask.Price ? formatNumber(ask.Price) : "0"}
                </td>
                <td
                  style={{
                    fontWeight: 600,
                    color: theme.primaryText,
                    fontSize: "0.75rem",
                    padding: "0.65rem 0.25rem",
                    backgroundColor: theme.primaryTint,
                    textAlign: "center",
                  }}
                >
                  {ask.NumberOfOrders || "0"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const StockCandleModal = ({
  isOpen,
  onClose,
  Exch,
  ExchType,
  ExchSeg,
  ScripCode,
  symbolToken,
  Name,
  Symbol,
  stock,
  onBuy,
  onSell,
}) => {
  const [range, setRange] = useState("1D");
  const [liveCurrentPrice, setLiveCurrentPrice] = useState(null);
  const [marketDepth, setMarketDepth] = useState([]);
  const fivePaisaPriceUnsubRef = useRef(null);
  const fivePaisaSubscriptionRef = useRef(null);
  const angelOnePriceUnsubRef = useRef(null);
  const angelOneSubscriptionRef = useRef(null);

  const toMarketFeedExchange = (exchange) => {
    const raw = String(exchange || "").toUpperCase();
    if (raw.startsWith("B")) return "B";
    if (raw.startsWith("M")) return "M";
    return "N";
  };
  const toMarketFeedExchangeType = (exchangeType) => {
    const raw = String(exchangeType || "").toUpperCase();
    return raw.startsWith("D") ? "D" : "C";
  };

  const [timeframe, setTimeframe] = useState("5m");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [candles, setCandles] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [stockPreview, setStockPreview] = useState(null);
  const previewAbortRef = useRef(null);
  const livePriceRef = useRef(null);
  const candlesFetchIdRef = useRef(0);
  const timeframeRef = useRef(timeframe);
  const rangeRef = useRef(range);

  timeframeRef.current = timeframe;
  rangeRef.current = range;

  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "{}");
  const brokerFlag = toPmsBrokerFlag(
    localStorage.getItem("broker_type") || sharedData?.broker_type || "5paisa",
  );
  const isGrowBroker = brokerFlag === "grow";
  const isAngelOneBroker = brokerFlag === "angelone";
  const isFivePaisaBroker = brokerFlag === "5paisa";
  const supportsStockPreview = isFivePaisaBroker || isAngelOneBroker;
  const resolvedSymbolToken = String(symbolToken || ScripCode || "").trim();
  const displayName = Name || Symbol || resolvedSymbolToken || ScripCode;
  const displaySymbol = Symbol || resolvedSymbolToken || ScripCode;
  const exchangeLabel = String(Exch || ExchSeg || "").toUpperCase() || "NSE";

  const fetchStockPreview = async () => {
    if (!supportsStockPreview) return;
    if (isFivePaisaBroker && !ScripCode) return;
    if (isAngelOneBroker && !resolvedSymbolToken) return;

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }
    const controller = new AbortController();
    previewAbortRef.current = controller;

    try {
      setPreviewLoading(true);
      setPreviewError(null);
      setStockPreview(null);

      const preview = await stockPreviewService.fetchPreview(
        {
          broker: brokerFlag,
          Exch,
          ExchType,
          ExchSeg,
          ScripCode,
          symbolToken: resolvedSymbolToken,
        },
        { signal: controller.signal },
      );

      if (controller.signal.aborted) return;
      setStockPreview(preview);
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error(err);
      setPreviewError(err?.message || "Failed to load market snapshot");
    } finally {
      if (!controller.signal.aborted) {
        setPreviewLoading(false);
      }
    }
  };

  const fetchCandles = async ({ silent = false } = {}) => {
    const MAX_RETRIES = silent ? 1 : 3;
    let attempt = 0;
    const fetchId = ++candlesFetchIdRef.current;

    while (attempt < MAX_RETRIES) {
      try {
        if (!silent) {
          setLoading(true);
          setError(null);
        }

        const shared = JSON.parse(localStorage.getItem("sharedData") || "{}");
        const user_id = Number(shared?.ID || localStorage.getItem("id") || 0);

        if (brokerFlag === "angelone" && !resolvedSymbolToken) {
          throw new Error("Missing AngelOne candle params");
        }

        const body = buildCandleRequestBody({
          brokerFlag,
          range,
          timeframe,
          user_id,
          Exch,
          ExchType,
          ExchSeg,
          ScripCode,
          symbolToken: resolvedSymbolToken,
        });

        const res = await fetch(
          "https://pmsapi.infomanav.in/api/v1/FNO?xAction=get_candles",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          },
        );

        const data = await res.json();

        if (!res.ok || data?.success !== true || !Array.isArray(data?.data)) {
          throw new Error("API failed");
        }

        if (fetchId !== candlesFetchIdRef.current) return;

        const transformed = transformCandles(data.data, range, brokerFlag);
        setCandles((prev) =>
          silent
            ? mergeFetchedCandlesWithLive(
                transformed,
                prev,
                livePriceRef.current,
                timeframe,
                range,
              )
            : applyLivePriceToCandles(transformed, livePriceRef.current),
        );
        return;
      } catch (err) {
        attempt++;

        if (attempt >= MAX_RETRIES) {
          console.error(err);
          if (!silent) setError("Failed to load data");
        } else {
          await new Promise((res) => setTimeout(res, 500));
        }
      } finally {
        if (!silent && fetchId === candlesFetchIdRef.current) {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (isOpen && isGrowBroker) {
      onClose();
    }
  }, [isOpen, isGrowBroker, onClose]);

  useEffect(() => {
    if (!isOpen || isGrowBroker) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, isGrowBroker]);

  useEffect(() => {
    if (isOpen && !isGrowBroker) {
      if (brokerFlag === "5paisa" && !ScripCode) return;
      if (isAngelOneBroker && !resolvedSymbolToken) return;
      fetchCandles();
    }
  }, [
    range,
    timeframe,
    isOpen,
    isGrowBroker,
    brokerFlag,
    isAngelOneBroker,
    ScripCode,
    resolvedSymbolToken,
    Exch,
    ExchType,
    ExchSeg,
  ]);

  // Live LTP subscription
  useEffect(() => {
    const clearFivePaisaSubscription = () => {
      if (fivePaisaPriceUnsubRef.current) {
        fivePaisaPriceUnsubRef.current();
        fivePaisaPriceUnsubRef.current = null;
      }
      if (fivePaisaSubscriptionRef.current) {
        fivePaisaWebSocketService.unsubscribeMarketFeed([
          fivePaisaSubscriptionRef.current,
        ]);
        fivePaisaSubscriptionRef.current = null;
      }
    };

    const clearAngelOneSubscription = () => {
      if (angelOnePriceUnsubRef.current) {
        angelOnePriceUnsubRef.current();
        angelOnePriceUnsubRef.current = null;
      }
      if (angelOneSubscriptionRef.current) {
        angelOneWebSocketService.unsubscribeMarketFeed([
          angelOneSubscriptionRef.current,
        ]);
        angelOneSubscriptionRef.current = null;
      }
    };

    clearFivePaisaSubscription();
    clearAngelOneSubscription();
    setLiveCurrentPrice(null);

    if (!isOpen || isGrowBroker) return () => {};

    if (brokerFlag === "5paisa" && ScripCode) {
      const scripCode = Number(ScripCode);
      if (!scripCode) return () => {};

      const subItem = {
        Exch: toMarketFeedExchange(Exch),
        ExchType: toMarketFeedExchangeType(ExchType),
        ScripCode: scripCode,
      };
      fivePaisaSubscriptionRef.current = subItem;

      fivePaisaPriceUnsubRef.current = fivePaisaWebSocketService.onPrice(
        (tick) => {
          if (!tick) return;
          if (Number(tick?.Token) !== scripCode) return;
          const ltp = Number(tick?.LastRate);
          if (Number.isFinite(ltp)) setLiveCurrentPrice(ltp);
        },
      );

      fivePaisaWebSocketService.connect();
      fivePaisaWebSocketService.subscribeMarketFeed([subItem]);
    }

    if (isAngelOneBroker && resolvedSymbolToken) {
      const exchangeType = getAngelExchangeTypeFromHolding({
        Exch,
        ExchSeg,
        ExchType,
      });
      const subItem = { exchangeType, token: resolvedSymbolToken };
      angelOneSubscriptionRef.current = subItem;

      angelOnePriceUnsubRef.current = angelOneWebSocketService.onPrice(
        (tick) => {
          if (!tick) return;
          if (String(tick?.Token) !== resolvedSymbolToken) return;
          const ltp = Number(tick?.LastRate);
          if (Number.isFinite(ltp)) setLiveCurrentPrice(ltp);
        },
      );

      angelOneWebSocketService.connect();
      angelOneWebSocketService.subscribeMarketFeed([subItem]);
    }

    return () => {
      clearFivePaisaSubscription();
      clearAngelOneSubscription();
    };
  }, [
    isOpen,
    ScripCode,
    brokerFlag,
    isGrowBroker,
    isAngelOneBroker,
    Exch,
    ExchType,
    ExchSeg,
    resolvedSymbolToken,
    Symbol,
  ]);

  useEffect(() => {
    const canFetch =
      isOpen &&
      supportsStockPreview &&
      ((isFivePaisaBroker && ScripCode) ||
        (isAngelOneBroker && resolvedSymbolToken));

    if (!canFetch) {
      setStockPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return undefined;
    }

    fetchStockPreview();

    return () => {
      if (previewAbortRef.current) {
        previewAbortRef.current.abort();
        previewAbortRef.current = null;
      }
    };
  }, [
    isOpen,
    supportsStockPreview,
    isFivePaisaBroker,
    isAngelOneBroker,
    Exch,
    ExchType,
    ExchSeg,
    ScripCode,
    resolvedSymbolToken,
    brokerFlag,
  ]);

  // Market depth for preview page (5paisa WS store / AngelOne polling service)
  useEffect(() => {
    if (!isOpen) {
      setMarketDepth([]);
      return undefined;
    }

    if (isFivePaisaBroker && ScripCode) {
      const instrument = stock || {
        Exch,
        ExchType,
        ScripCode,
        exch: Exch,
        exchType: ExchType,
        scripcode: ScripCode,
      };

      fivePaisaMarketDepthStore.openDepth(instrument);

      const unsub = fivePaisaMarketDepthStore.subscribe((snap) => {
        setMarketDepth(
          Array.isArray(snap?.activeDepthData) ? snap.activeDepthData : [],
        );
      });

      return () => {
        unsub();
        fivePaisaMarketDepthStore.closeDepth();
        setMarketDepth([]);
      };
    }

    if (isAngelOneBroker && resolvedSymbolToken) {
      const shared = JSON.parse(localStorage.getItem("sharedData") || "{}");
      const userId = Number(shared?.ID || localStorage.getItem("id") || 0);
      const holding = stock || {
        Exch,
        ExchSeg,
        ExchType,
        symboltoken: resolvedSymbolToken,
        token: resolvedSymbolToken,
        ScripCode: resolvedSymbolToken,
      };

      angelOneMarketDepthService.openDepth(holding, {
        pmsApiBaseUrl: PMS_API_BASE,
        userId,
      });

      const unsub = angelOneMarketDepthService.subscribe((depth) => {
        setMarketDepth(Array.isArray(depth) ? depth : []);
      });

      return () => {
        unsub();
        angelOneMarketDepthService.closeDepth();
        setMarketDepth([]);
      };
    }

    setMarketDepth([]);
    return undefined;
  }, [
    isOpen,
    isFivePaisaBroker,
    isAngelOneBroker,
    Exch,
    ExchType,
    ExchSeg,
    ScripCode,
    resolvedSymbolToken,
    stock,
  ]);

  const resolvedLivePrice = useMemo(() => {
    if (Number.isFinite(liveCurrentPrice) && liveCurrentPrice > 0)
      return liveCurrentPrice;
    return candles[candles.length - 1]?.close || 0;
  }, [liveCurrentPrice, candles]);

  useEffect(() => {
    livePriceRef.current = liveCurrentPrice;
  }, [liveCurrentPrice]);

  useEffect(() => {
    if (!Number.isFinite(liveCurrentPrice) || liveCurrentPrice <= 0) return;
    setCandles((prev) =>
      rollLiveCandleIfNeeded(
        prev,
        liveCurrentPrice,
        timeframeRef.current,
        rangeRef.current,
      ),
    );
  }, [liveCurrentPrice]);

  // TF-aligned live candle rollover + silent API sync
  useEffect(() => {
    if (!isOpen || isGrowBroker) return undefined;
    if (brokerFlag === "5paisa" && !ScripCode) return undefined;
    if (isAngelOneBroker && !resolvedSymbolToken) return undefined;

    const tfMs = getTimeframeMs(timeframe);
    // Intraday TF only — daily candles don't need minute-level rollover
    if (!tfMs || timeframe === "1d") return undefined;

    let cancelled = false;
    let timeoutId = null;

    const runBoundaryTick = () => {
      if (cancelled) return;

      setCandles((prev) =>
        rollLiveCandleIfNeeded(
          prev,
          livePriceRef.current,
          timeframeRef.current,
          rangeRef.current,
        ),
      );

      // Silent refresh so new candle comes from API without chart flash/loading
      fetchCandles({ silent: true });

      timeoutId = setTimeout(runBoundaryTick, getMsUntilNextBucket(tfMs));
    };

    timeoutId = setTimeout(runBoundaryTick, getMsUntilNextBucket(tfMs));

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    isOpen,
    isGrowBroker,
    brokerFlag,
    isAngelOneBroker,
    ScripCode,
    resolvedSymbolToken,
    timeframe,
    range,
    Exch,
    ExchType,
    ExchSeg,
  ]);

  const priceMeta = useMemo(() => {
    const prev = candles[0]?.open || 0;
    const diff = resolvedLivePrice - prev;
    const pct = prev > 0 ? ((diff / prev) * 100).toFixed(2) : "0.00";
    const isPos = diff >= 0;
    return { diff, pct, isPos };
  }, [candles, resolvedLivePrice]);

  const handleBuyClick = () => {
    if (typeof onBuy === "function") {
      onBuy(stock);
    }
  };

  const handleSellClick = () => {
    if (typeof onSell === "function") {
      onSell(stock);
    }
  };

  if (isGrowBroker) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            background: "#F5F7FB",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {/* Sticky header: name + LTP + Buy/Sell */}
            <div
              style={{
                flexShrink: 0,
                background: "#fff",
                borderBottom: "1px solid #E5E7EB",
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Back"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: "1px solid #E5E7EB",
                    background: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#334155",
                    flexShrink: 0,
                  }}
                >
                  <FiArrowLeft size={18} />
                </button>

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <h2
                      style={{
                        margin: 0,
                        fontSize: "1.15rem",
                        fontWeight: 700,
                        color: "#0F172A",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.2,
                      }}
                    >
                      {displayName}
                    </h2>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: DEPTH_THEME.primary,
                        background: DEPTH_THEME.primarySoft,
                        borderRadius: 6,
                        padding: "2px 8px",
                      }}
                    >
                      {exchangeLabel}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 10,
                      marginTop: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        color: "#64748B",
                        fontWeight: 600,
                      }}
                    >
                      {displaySymbol}
                    </span>
                    <span
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        color: "#0F172A",
                      }}
                    >
                      ₹{formatNumber(resolvedLivePrice)}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: priceMeta.isPos ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {priceMeta.isPos ? "+" : ""}₹
                      {Math.abs(priceMeta.diff).toFixed(2)} (
                      {priceMeta.isPos ? "+" : ""}
                      {priceMeta.pct}%)
                    </span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexShrink: 0,
                }}
              >
                {(typeof onBuy === "function" || typeof onSell === "function") && (
                  <div className="buy-sell-segmented">
                    {typeof onBuy === "function" && (
                      <button
                        className="btn_buy wl-premium-action-btn wl-premium-buy"
                        type="button"
                        onClick={handleBuyClick}
                      >
                        Buy
                      </button>
                    )}
                    {typeof onSell === "function" && (
                      <button
                        className="btn_sell wl-premium-action-btn wl-premium-sell"
                        type="button"
                        onClick={handleSellClick}
                      >
                        Sell
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: "none",
                    background: "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#64748B",
                  }}
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px 20px 28px",
              }}
            >
              {/* Chart card */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  border: "1px solid #E5E7EB",
                  overflow: "hidden",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    gap: 10,
                    borderBottom: "1px solid #F1F5F9",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {["1D", "1W", "1M", "6M"].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRange(r)}
                        style={{
                          padding: "6px 14px",
                          borderRadius: 8,
                          border: "none",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          background:
                            range === r
                              ? `linear-gradient(135deg, ${DEPTH_THEME.primary}, ${DEPTH_THEME.primaryDark})`
                              : "#F1F5F9",
                          color: range === r ? "#fff" : "#475569",
                        }}
                      >
                        {r}
                      </button>
                    ))}
                  </div>

                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        color: "#64748B",
                        fontWeight: 600,
                      }}
                    >
                      TF
                    </span>
                    <select
                      value={timeframe}
                      onChange={(e) => setTimeframe(e.target.value)}
                      style={{
                        borderRadius: 8,
                        border: "1px solid #E2E8F0",
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#334155",
                        background: "#FFFFFF",
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      {["1m", "5m", "10m", "15m", "30m", "60m", "1d"].map(
                        (tf) => (
                          <option key={tf} value={tf}>
                            {tf}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </div>

                <div style={{ height: 420, padding: 10 }}>
                  {loading ? (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 12,
                        color: "#475569",
                      }}
                    >
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 999,
                          border: "3px solid #DBEAFE",
                          borderTopColor: DEPTH_THEME.primary,
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        Loading chart data...
                      </div>
                      <style>
                        {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
                      </style>
                    </div>
                  ) : error ? (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        textAlign: "center",
                        color: "#64748B",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#DC2626",
                        }}
                      >
                        {error}
                      </div>
                      <button
                        type="button"
                        onClick={fetchCandles}
                        style={{
                          border: "none",
                          borderRadius: 8,
                          padding: "7px 16px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          background: `linear-gradient(135deg, ${DEPTH_THEME.primary}, ${DEPTH_THEME.primaryDark})`,
                          color: "#fff",
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <RealCandlestickChart data={candles} range={range} />
                  )}
                </div>
              </div>

              {/* Below chart: snapshot + depth side by side */}
              {supportsStockPreview && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1.7fr) minmax(300px, 0.85fr)",
                    gap: 12,
                    alignItems: "start",
                  }}
                  className="stock-preview-below-chart"
                >
                  <section
                    style={{
                      background: "#fff",
                      borderRadius: 10,
                      border: "1px solid #DCE3EF",
                      padding: "12px 14px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        marginBottom: 10,
                        gap: 8,
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#1A2332",
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        Market Snapshot
                        <SectionInfoIcon text="Live price, change, volume, session ranges, order flow, and key trading levels for this symbol." />
                      </h3>
                    </div>
                    <StockPreviewMarketSnapshotPanel
                      marketSnapshot={stockPreview?.marketSnapshot}
                      loading={previewLoading}
                      error={previewError}
                      onRetry={fetchStockPreview}
                    />
                  </section>

                  <section
                    style={{
                      background: "#fff",
                      borderRadius: 10,
                      border: "1px solid #DCE3EF",
                      padding: 12,
                      position: "sticky",
                      top: 8,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 10,
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#1A2332",
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        Market Depth
                        <SectionInfoIcon text="Top 5 bid and ask price levels with quantity and order count, updated in real time from your broker feed." />
                      </h3>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#6B7A90",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: marketDepth.length ? "#1B7F4E" : "#94A3B8",
                          }}
                        />
                        Live
                      </span>
                    </div>
                    <MarketDepthPanel depthRows={marketDepth} />
                  </section>

                  <style>
                    {`
                      @media (max-width: 1100px) {
                        .stock-preview-below-chart {
                          grid-template-columns: 1fr !important;
                        }
                      }
                    `}
                  </style>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StockCandleModal;
